import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { Plus } from 'lucide-react';
import { FormModal } from '@/components/modal/FormModal';
import { haptic } from '@/utils/haptics';
import type { WidgetDef, WidgetCategory } from './widgetRegistry';

interface WidgetAddDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  widgets: WidgetDef[];
  onAdd: (id: string) => void;
}

const CATEGORY_ORDER: WidgetCategory[] = ['financial', 'reports', 'activity', 'charts', 'support', 'admin'];

export function WidgetAddDrawer({ isOpen, onClose, widgets, onAdd }: WidgetAddDrawerProps) {
  const { t } = useTranslation();

  const handleAdd = (id: string) => {
    haptic.light();
    onAdd(id);
    if (widgets.length <= 1) onClose();
  };

  const grouped = CATEGORY_ORDER.map((cat) => ({
    category: cat,
    items: widgets.filter((w) => w.category === cat),
  })).filter((g) => g.items.length > 0);

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('home.widgets.addWidget')}
    >
      <div className="space-y-5">
        {grouped.map(({ category, items }) => (
          <div key={category}>
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
              {t(`home.widgets.categories.${category}`)}
            </h4>
            <div className="grid grid-cols-3 gap-3">
              {items.map((widget) => {
                const Icon = widget.icon;
                return (
                  <motion.button
                    key={widget.id}
                    className="flex flex-col items-center gap-2 rounded-2xl bg-white/50 p-3 backdrop-blur-sm transition-colors hover:bg-white/70"
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleAdd(widget.id)}
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-50">
                      <Icon size={22} className="text-primary-600" strokeWidth={1.8} />
                    </div>
                    <span className="text-xs font-medium text-gray-700">
                      {t(widget.labelKey)}
                    </span>
                    <span className="flex items-center gap-0.5 text-[10px] font-medium text-primary-600">
                      <Plus size={10} />
                      {t('home.dock.add')}
                    </span>
                  </motion.button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </FormModal>
  );
}
